import { add, isAfter, isBefore } from "date-fns";
import { computed, ComputedRef, Ref, ref, toRefs, watch } from "vue";
import { dnd } from "vue-easy-dnd";

import { Controller, Event, EventId, Pause, Week } from "@ctrl/controller-rpc";

import { DraggedAllocation, DraggedTask } from "@/types";
import { isBoundTaskId } from "@/utils/id";

type Position = {
  x: number;
  y: number;
};

/**
 * The task sections a task or allocation drag can be dropped on to
 * reschedule it (Overdue is display-only). Shared by the Agenda's task pane
 * and the canvas Tasks widget.
 */
export type DroppableTaskSection =
  | "today"
  | "week"
  | "upcoming"
  | "unplanned"
  | "completed"
  | "discarded";

/**
 * Refuse week-scheduling drops (Week and Upcoming sections) for bound
 * tasks, which cannot be scheduled to a week — team tasks (database rows,
 * `object:…` ids) and integration tasks (native ids with `binding` set)
 * alike. Task drags carry a precomputed `isBound` (the slim payload has no
 * `binding`); allocation drags carry the full task record, checked here
 * like `isBoundTask`.
 */
export const acceptsWeekPlannable = (data: {
  task?: { id?: string; binding?: unknown; isBound?: boolean };
}): boolean => {
  const task = data?.task;
  if (!task) return true;
  return !(
    task.isBound ??
    (task.binding !== undefined || isBoundTaskId(task.id ?? ""))
  );
};

/**
 * Apply a task or allocation drop on a task section: reschedule the task to
 * the section's target (the day, its week, next week, unplanned, completed,
 * or discarded). An allocation drop first deletes its scheduled event —
 * only the task remains. Same-section row drops are the caller's no-op
 * (handle reorder is Ordering-only).
 */
export function dropPlannableOnSection(
  controller: Controller,
  e: DraggedAllocation | DraggedTask,
  section: DroppableTaskSection,
  context: { date: Date; week: Week; nextWeek: Week }
): void {
  if (!e.success) return;
  if (e.type === "allocation") {
    // Remove the event associated with the allocation.
    controller.event.delete(e.data.event.id, false, "app");
  }
  const id = e.data.task.id;
  switch (section) {
    case "today": {
      controller.task.update(
        id,
        { completed: null, scheduled: context.date },
        "app",
        null
      );
      break;
    }
    case "week": {
      controller.task.update(
        id,
        { completed: null, scheduled: context.week },
        "app",
        null
      );
      break;
    }
    case "upcoming": {
      controller.task.update(
        id,
        { completed: null, scheduled: context.nextWeek },
        "app",
        null
      );
      break;
    }
    case "unplanned": {
      controller.task.update(
        id,
        { completed: null, scheduled: null },
        "app",
        null
      );
      break;
    }
    case "completed": {
      controller.task.update(id, { completed: new Date() }, "app", null);
      break;
    }
    case "discarded": {
      controller.task.update(id, { ignored: new Date() }, "app", null);
      break;
    }
  }
}

export type TemporaryEvent = {
  id: EventId;
  start: Date;
  end: Date;
};

export type DndEvent = {
  data: Event;
  native: MouseEvent;
  position: Position;
  source: unknown;
  success: boolean;
  top: unknown;
  type: string;
};

export function isDraggingEvent() {
  return dnd.inProgress && dnd.type === "event";
}

export function isDraggingAllocation() {
  return dnd.inProgress && dnd.type === "allocation";
}

export function isDraggingTask() {
  return dnd.inProgress && dnd.type === "task";
}

export function useDndEvent() {
  if ((dnd.inProgress && dnd.type === "event") || dnd.type === "allocation")
    return dnd.data;
  return null;
}

export function useEventDragging(
  eventId: string,
  max: ComputedRef<{ start: Date | undefined; end: Date | undefined }>,
  eventsEl: Ref<HTMLElement | undefined>
): {
  initialPosition: Ref<Position | undefined>;
  draggingStyle: Ref<Record<string, string> | undefined>;
  draggingEvent: Ref<TemporaryEvent | undefined>;
  release: () => void;
} {
  const { data, position } = toRefs(dnd);
  const initialPosition = ref<Position>();
  const initialScrollTop = ref(0);
  const draggingStyle = ref<Record<string, string>>();
  const draggingEvent = ref<TemporaryEvent>();

  const stopWatchPosition = watch(
    () => position.value,
    (position, prevPosition) => {
      if (prevPosition && !position) return clear();
      if (!data.value || data.value.id !== eventId) return;
      if (position.x < 550) {
        draggingStyle.value = undefined;
        return;
      }

      if (!prevPosition) {
        initialPosition.value = { x: position.x, y: position.y };
        initialScrollTop.value = eventsEl.value?.scrollTop || 0;
        return;
      }

      if (!initialPosition.value?.y) return;

      // Calculate scroll difference since drag started
      const currentScrollTop = eventsEl.value?.scrollTop || 0;
      const scrollDiff = currentScrollTop - initialScrollTop.value;

      // Adjust pixel difference to account for scroll changes
      const pixelDiff = position.y - initialPosition.value.y + scrollDiff;
      const draggingStart = add(data.value.start, {
        minutes: Math.floor(pixelDiff / 10) * 15,
      });
      const draggingEnd = add(data.value.end, {
        minutes: Math.floor(pixelDiff / 10) * 15,
      });

      if (
        (max.value.end && isAfter(draggingEnd, max.value.end)) ||
        (max.value.start && isBefore(draggingStart, max.value.start))
      ) {
        return;
      }

      draggingStyle.value = {
        transform: `translate(0, ${Math.floor(pixelDiff / 10) * 10}px)`,
      };

      draggingEvent.value = {
        id: data.value.id,
        start: draggingStart,
        end: draggingEnd,
      };
    }
  );

  function clear() {
    draggingStyle.value = undefined;
    draggingEvent.value = undefined;
    initialPosition.value = undefined;
    initialScrollTop.value = 0;
  }

  const release = () => {
    stopWatchPosition();
  };

  return {
    initialPosition,
    draggingStyle,
    draggingEvent,
    release,
  };
}
export const useDropEvent = (
  event: Ref<Pause>,
  placeholderPosition: Ref<number | null | undefined>
): {
  computeDropTime: (startDate: Date, position: number) => Date;
  dropTime: ComputedRef<Date | undefined>;
  dropDuration: ComputedRef<number | undefined>;
} => {
  const computeDropTime = (startDate: Date, position: number) =>
    add(startDate, { minutes: Math.floor(position / 10) * 15 });
  const dropTime: ComputedRef<Date | undefined> = computed(() =>
    placeholderPosition.value !== null &&
    placeholderPosition.value !== undefined
      ? computeDropTime(event.value.start, placeholderPosition.value)
      : undefined
  );
  const dropDuration: ComputedRef<number | undefined> = computed(() =>
    dropTime.value !== null && dropTime.value !== undefined
      ? (new Date(event.value.end).getTime() - dropTime.value.getTime()) /
        (1000 * 60)
      : undefined
  );
  return { computeDropTime, dropTime, dropDuration };
};
